import { getAllTenants, type TenantRead } from "@harmonyso/tenants-sdk";
import {
  CELLS,
  PRIMARY_CELL,
  cellListRequestOptions,
  findCell,
  isRemoteCell,
  type Cell,
} from "./cells";
import { CellSignInRequiredError, isCellSignInError } from "./cell-session";
import { mergeCellTenants, type CellFailure } from "./cell-tenants";

export class TenantCellUnknownError extends Error {
  /** True when every cell answered and none has the tenant. */
  readonly definite: boolean;

  constructor(tenantId: string, unreachableCells: string[]) {
    super(
      unreachableCells.length > 0
        ? `Couldn't tell which cell tenant ${tenantId} is in: ${unreachableCells.join(", ")} didn't answer.`
        : `Tenant ${tenantId} isn't in any cell.`,
    );
    this.name = "TenantCellUnknownError";
    this.definite = unreachableCells.length === 0;
  }
}

/**
 * The tenant isn't in any cell that answered, and another cell that might
 * have it needs its own sign-in first.
 */
export class TenantNotFoundSignedInCellsError extends CellSignInRequiredError {
  constructor(tenantId: string, cellId: string) {
    super(cellId);
    this.message = `Tenant ${tenantId} wasn't found. ${cellId} wasn't searched because you aren't signed in to it.`;
    this.name = "TenantNotFoundSignedInCellsError";
  }
}

const UNKNOWN_TENANT_TTL_MS = 30_000;

/** Tenants no cell could place, so a burst of requests doesn't re-list every cell. */
const unknownTenants = new Map<
  string,
  { error: TenantCellUnknownError; until: number }
>();

/** Which cell each known tenant lives in, filled from every merged tenant list. */
const tenantCellIds = new Map<string, string>();

export function rememberTenantCells(
  tenants: Pick<TenantRead, "id" | "deployment_cell">[],
): void {
  for (const tenant of tenants) {
    if (tenant.deployment_cell) {
      tenantCellIds.set(tenant.id, tenant.deployment_cell);
      unknownTenants.delete(tenant.id);
    }
  }
}

let pendingLookup: Promise<CellFailure[]> | null = null;

/** Lists every cell's tenants and returns the cells that failed. */
async function lookUpAllCells(): Promise<CellFailure[]> {
  const results = await Promise.all(
    CELLS.map(async (cell) => {
      try {
        const { data } = await getAllTenants({
          ...cellListRequestOptions(cell.id),
          throwOnError: true,
        });
        return { cell, tenants: data };
      } catch (error) {
        return { cell, error: error as Error };
      }
    }),
  );
  const { tenants, failures } = mergeCellTenants(results);
  rememberTenantCells(tenants);
  return failures;
}

/**
 * The cell a tenant lives in. With a single cell this is always the primary
 * one. Otherwise an unknown tenant triggers one lookup across every cell, and
 * a tenant that still isn't placed makes this throw rather than guess, so a
 * request is never sent to the wrong cell.
 */
export async function resolveTenantCell(tenantId: string): Promise<Cell> {
  if (CELLS.length === 1) {
    return PRIMARY_CELL;
  }

  let cell = findCell(tenantCellIds.get(tenantId));
  if (cell) {
    return cell;
  }
  const knownUnknown = unknownTenants.get(tenantId);
  if (knownUnknown) {
    if (knownUnknown.until > Date.now()) {
      throw knownUnknown.error;
    }
    unknownTenants.delete(tenantId);
  }

  pendingLookup ??= lookUpAllCells().finally(() => {
    pendingLookup = null;
  });
  const unreachable = await pendingLookup;

  cell = findCell(tenantCellIds.get(tenantId));
  if (cell) {
    return cell;
  }
  const primaryAnswered = !unreachable.some(
    ({ cell: failedCell }) => failedCell.id === PRIMARY_CELL.id,
  );
  const signInFailure = unreachable.find(
    ({ cell: failedCell, error }) =>
      isRemoteCell(failedCell) && isCellSignInError(error),
  );
  // Only ask for another cell's sign-in once the primary cell has answered
  // without the tenant; otherwise its own failure is the likelier reason.
  if (signInFailure && primaryAnswered) {
    throw new TenantNotFoundSignedInCellsError(tenantId, signInFailure.cell.id);
  }
  const error = new TenantCellUnknownError(
    tenantId,
    unreachable.map(({ cell: { id } }) => id),
  );
  // Only a definite answer is remembered: with every cell listed, the tenant
  // is in none of them. A cell that failed may simply have been unavailable.
  if (unreachable.length === 0) {
    unknownTenants.set(tenantId, {
      error,
      until: Date.now() + UNKNOWN_TENANT_TTL_MS,
    });
  }
  throw error;
}

export class ConflictingTenantsError extends Error {
  constructor(tenantIds: string[]) {
    super(
      `Request names more than one tenant (${tenantIds.join(", ")}); not sent.`,
    );
    this.name = "ConflictingTenantsError";
  }
}

/** A routing failure that retrying the same request can't fix. */
export const isCellRoutingError = (
  error: unknown,
): error is
  | TenantCellUnknownError
  | CellSignInRequiredError
  | ConflictingTenantsError =>
  (error instanceof TenantCellUnknownError && error.definite) ||
  error instanceof CellSignInRequiredError ||
  error instanceof ConflictingTenantsError;
